import { ReactNode } from 'react';
// 组件
import { DeleteOutlined } from '@ant-design/icons';
import { ProTooltip, ProImage } from '../../antd';
import Images from '../../Images';
import RouteLink from '../../RouteLink';
// 方法
import usePrevious from '../../Hooks/usePrevious';
import { useLayoutContext } from '../../PageLayout/context/LayoutContext';
import { renderEnumNames } from '../../utils/renderUtils';
import { getColumnValue } from '../../utils/dataUtils';
// 常量
import { ColumnType } from 'antd/lib/table';
import {
  MainSubEdiTableProps,
  EditColumnType,
  MainSubEdiTableDispatchType,
  IMainSubEdiTablePayload,
} from '../interface';
import { TABLE_COLUMN_EMPTY } from '../../constants/constant';

function ediTableColumnsHook(
  props: MainSubEdiTableProps,
  dispatchEdiTable: (
    type: MainSubEdiTableDispatchType,
    payload: IMainSubEdiTablePayload,
    ) => void,
  dataSource?: any[],
) {
  const {
    columns,
    disabled,
    operation,
    onDelete,
  } = props;
  const { enumData } = useLayoutContext();
  const prevDisabled = usePrevious(disabled);
  const handleDelete = async (index: number, record: any) => {
    if (onDelete) {
      const result = await onDelete(index, record);
      if (!result) return;
    }
    dispatchEdiTable('deleteRow', { record: record });
  };
  const rowSpanColumnsProps = (record: any, isRowSpan?: boolean): EditColumnType => {
    const { rowSpan, } = record;
    if (isRowSpan) {
      return { rowSpan: rowSpan === undefined ? 1 : rowSpan, };
    }
    return {};
  };
  const formatColumns = (
    columnsProps: EditColumnType[],
    isChildren: boolean = false,
  ): EditColumnType[] => {
    let newColumns: EditColumnType[] = [];
    if (Array.isArray(columnsProps) && columnsProps.length) {
      const baseColumnsProps: EditColumnType = {
        ellipsis: true,
      };
      // @ts-ignore
      newColumns = columnsProps?.map((column, index) => {
        const {
          editable,
          title,
          dataIndex,
          enumCode,
          renderType,
          children,
          fixed,
          required,
          relativePathName,
          isRowSpan,
          routeLinkProps,
          disabled: columnDisabled,
          ...restProps
        } = column;
        // const baseUpdateColumnsProps: EditColumnType = {};
        const baseUpdateColumnsProps: EditColumnType = fixed
          ? {}
          : {
              shouldCellUpdate: (record, prevRecord) => {
                if (!dataIndex) return true;
                if (record.isMainTable) return true;
                if (disabled !== prevDisabled) return true;
                if (record.rowSpan || prevRecord.rowSpan) {
                  return record.rowSpan !== prevRecord.rowSpan;
                }
                if (typeof dataIndex === 'string') {
                  if (isChildren) return record?.children?.[dataIndex] !== prevRecord?.children?.[dataIndex];
                  return record[dataIndex] !== prevRecord[dataIndex];
                }
                return false;

              },
            };
        if (editable) {
          return {
            ...baseColumnsProps,
            ...column,
            ...baseUpdateColumnsProps,
            title: required ? (
              <div className="edit-table-column-required">{title as ReactNode}</div>
            ) : (
              title
            ),
            onCell: (record: any, recordIndex?: number) => {
              let initialValue: any;
              if (typeof dataIndex === 'string')
                initialValue = record[dataIndex];
              if (
                restProps.formItemType === 'SelectTable' &&
                Array.isArray(dataIndex)
              ) {
                initialValue = record[dataIndex[0]];
              }
              if (restProps.formItemType === 'SelectCombination' && Array.isArray(dataIndex)) {
                initialValue = [record[dataIndex[0]], record[dataIndex[1]]];
              }
              if (!initialValue && restProps.initialValue !== undefined)
                initialValue = restProps.initialValue;
              return {
                record,
                editable,
                disabled: columnDisabled || disabled,
                formItemProps: {
                  relativePathName,
                  enumCode,
                  required,
                  label: title,
                  name: dataIndex,
                  ...restProps,
                  initialValue,
                },
                ...rowSpanColumnsProps(record, isRowSpan),
              };
            },
          };
        }
        
        if (enumCode && enumData) {
          const enumItem = enumData[`${enumCode}`];
          return enumItem
            ? {
                ...baseColumnsProps,
                ...column,
                onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
                render: (val: any, record: any) => {
                  return renderEnumNames(val, enumItem.items);
                },
              }
            : { ...baseColumnsProps, ...column };
        }
        if (Array.isArray(dataIndex)) {
          return {
            ...baseColumnsProps,
            ...column,
            onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
            render: (val: any, record: any) => {
              return (
                <>
                  {dataIndex.map(item => (
                    <ProTooltip
                      key={item}
                      placement="topLeft"
                      title={record[item]}
                      mouseEnterDelay={1}
                    >
                      <div className="ant-table-cell-ellipsis">
                        {record[item] || TABLE_COLUMN_EMPTY}
                      </div>
                    </ProTooltip>
                  ))}
                </>
              );
            },
          };
        }
        switch (renderType) {
          case 'image':
            return {
              ...baseColumnsProps,
              ...column,
              onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
              render: (val: any, record: any) => {
                return <ProImage className="table-column-image" src={val} />;
              },
            };
          case 'images':
            return {
              ...column,
              width: column.width || 280,
              onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
              render: (val: any, record: any) => {
                if (Array.isArray(val)) return <Images images={val} />;
                return null;
              },
            };
          case 'boolean':
            return {
              ...baseColumnsProps,
              ...column,
              render: (val: any, record: any) => {
                return val ? '是' : '否';
              },
              onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
            };
          case 'routeLink':
            return {
              ...baseColumnsProps,
              ...column,
              render: (_: any, record: any, key: number) => {
                const columnValue = getColumnValue(record, dataIndex);
                const routeLinkQuery = routeLinkProps?.query;
                const routeLinkState = routeLinkProps?.state;
                return (
                  <RouteLink
                    text={columnValue}
                    {...routeLinkProps}
                    query={typeof routeLinkQuery === 'function' ? routeLinkQuery(record) : routeLinkQuery}
                    state={typeof routeLinkState === 'function' ? routeLinkState(record) : routeLinkState}
                  />
                );
              },
            };
        }
        return {
          ...baseColumnsProps,
          ...baseUpdateColumnsProps,
          ...column,
          children: children ? formatColumns(children, true) : undefined,
          render: (val: any, record: any, key: number) => {
            if (val === undefined || val === '' || val === null) {
              return column.render
                ? column.render(val, record, key)
                : TABLE_COLUMN_EMPTY;
            }
            if (typeof val === 'string') {
              const columnHTML = column.render ? column.render(val, record, key) : val;
              return (
                <ProTooltip placement="topLeft" title={val}>
                  {columnHTML as ReactNode}
                </ProTooltip>
              );
            }
            return column.render ? column.render(val, record, key) : val;
          },
          onCell: (record: any) => rowSpanColumnsProps(record, isRowSpan),
        };
      });
    }
    if (operation !== false && !disabled) {
      const operationColumns: ColumnType<any> = operation ? {
        ...operation,
        render: (record: any, _: any, key: number) => {
          return operation.render?.(record, dataSource, key);
        },
      } : {
        fixed: 'right',
        width: 50,
        align: 'center',
        title: '操作',
        render: (_: any, record: any, key: number) => {
          if (record.isMainTable) {
            return {
              children: '',
              props: {
                colSpan: 0,
                className: 'yb-table-row-main',
              },
            };
          }
          return (
            <>
              <DeleteOutlined
                className='text-danger'
                onClick={() => handleDelete(key, record)}
                title="删除"
              />
            </>
          );
        },
      };
      newColumns.push(operationColumns);
    }
    return newColumns;
  };
  return {
    columns: formatColumns(columns || []),
  };
}

export default ediTableColumnsHook;
